/* eslint-disable camelcase */
import React, { useEffect, useContext, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { gql, useQuery, ApolloProvider, useApolloClient } from '@apollo/client';
import { useSelector, Provider, useStore } from 'react-redux';
import useStyles from 'isomorphic-style-loader/useStyles';
import Skeleton from 'react-loading-skeleton';
import StyleContext from 'isomorphic-style-loader/StyleContext';
import { StylesProvider, ThemeProvider, createTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

// app modules: components
import Button from 'components/Button';
import ContentOptions from 'components/ContentOptions';
import ErrorRouter from 'components/ErrorRouter';
import ContentItems from 'components/ContentItems';
import HeadContent from 'components/HeadContent';
import Heading from 'components/Heading';
import InstagramPost from 'components/InstagramPost';
import Link from 'components/Link';
import Icon from 'components/Icon';
import { ListItem, UnorderedList } from 'components/List';
import Offline from 'components/Offline';

// app modules: hooks
import usePriceInjector from 'hooks/usePriceInjector';

// app modules: services
import { isApp, isAndroidApp, isIosApp } from 'services/environment';
import { longDate } from 'services/formatter';
import history from 'services/history';
import route from 'services/route';

// app: constants
import { COLOURS } from 'constants/index';

// app: assets
import createCkbkTheme from 'components/Layout/create-ckbk-theme';
import CssList from 'components/List/List.scss';
import CssBlogPost from './BlogPost.scss';

const GET_POST = gql`
  query GetBlogPost($id: String) {
    BlogPost(id: $id) {
      asset_url
      body
      categories
      description
      id
      publish_on
      scripts
      tags
      title
      url_id
    }
  }
`;

function BlogPost(props) {
  const { id } = props;
  useStyles(CssBlogPost, CssList);
  const { insertCss } = useContext(StyleContext);
  const apolloClient = useApolloClient();
  const store = useStore();
  const containerRef = useRef(null);
  usePriceInjector(containerRef);
  const [loadedScripts, setLoadedScripts] = useState([]);

  const baseUrl = useSelector(state => state.config.baseUrl);
  const { loading, error, data, refetch } = useQuery(GET_POST, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const osPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const theme = useSelector(state => state.config.theme);
  const prefersDarkMode = theme === 'dark' || (theme === 'system' && osPrefersDark);

  const muiTheme = React.useMemo(
    () => {
      return createTheme(createCkbkTheme(prefersDarkMode));
    },
    [prefersDarkMode],
  );

  // Pre-approved scripts can run
  useEffect(
    () => {
      if (!error && !loading && data && data.BlogPost) {
        const { scripts } = data.BlogPost;
        if (Array.isArray(scripts)) {
          scripts.forEach(script => {
            const existing = document.querySelectorAll(`script[src="${script}"]`);
            if (!existing.length) {
              const scriptEl = document.createElement('script');
              scriptEl.src = script;
              scriptEl.onload = () => {
                setLoadedScripts([...loadedScripts, script]);
              };
              document.body.appendChild(scriptEl);
            }
          });
        }
      }
      return () => {
        if (!error && !loading && data && data.BlogPost) {
          const { scripts } = data.BlogPost;
          if (Array.isArray(scripts)) {
            scripts.forEach(script => {
              const existing = document.querySelectorAll(`script[src="${script}"]`);
              if (existing.length) {
                document.body.removeChild(existing[0]);
              }
            });
          }
          // Workaround for the Riddle quiz not supporting unload.
          if (window.riddleAPI) {
            window.riddleAPI.riddles = [];
          }
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, loading, error, data],
  );
  // Add real content tiles in place of the embed placeholder divs.
  useEffect(() => {
    const embeds = document.querySelectorAll('.ckbk-blog-embed');
    const roots = Array.from(embeds).map(embed => {
      const root = createRoot(embed);
      root.render(
        <StyleContext.Provider value={{ insertCss }}>
          <Provider store={store}>
            <ApolloProvider client={apolloClient}>
              <StylesProvider injectFirst>
                <ThemeProvider theme={muiTheme}>
                  <ContentItems hideActions={false} watermarked={false} {...embed.dataset} />
                </ThemeProvider>
              </StylesProvider>
            </ApolloProvider>
          </Provider>
        </StyleContext.Provider>,
      );
      return root;
    });
    return () => {
      roots.forEach(root => root.unmount());
    };
  });

  // Generate iframes for try-interact apps
  useEffect(() => {
    const interactDivs = document.querySelectorAll('div[id^="interact-"]');
    Array.from(interactDivs).forEach(div => {
      if (div.childNodes.length === 0) {
        const appId = div.id.replace('interact-', '');
        const iframe = document.createElement('iframe');
        iframe.id = `tryinteract-${appId}`;
        iframe.src = `https://${div.dataset.host}/#/${appId}?embed=${appId}&auto_resize=1&origin=*`;
        iframe.width = '100%';
        iframe.height = parseInt(div.dataset.height, 10) || 375;
        iframe.class = 'blog-post__try-interact';
        iframe.scrolling = 'no';
        div.appendChild(iframe);
      }
    });
  });

  // Handle resizeHeight commands sent from try-interact iframes
  useEffect(() => {
    const handleMessage = event => {
      const { data: message, origin } = event;

      if (origin.endsWith('.tryinteract.com')) {
        const [command, appId, value] = message.split('|');
        if (command === 'resizeHeight') {
          const iframe = document.getElementById(`tryinteract-${appId}`);
          if (iframe) {
            iframe.style.height = `${value}px`;
          }
        }
      }
    };
    window.addEventListener('message', handleMessage, false);
    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  });

  useEffect(() => {
    Array.from(document.getElementsByClassName(isApp() ? 'hide-on-app' : 'hide-on-web')).forEach(
      el => {
        // eslint-disable-next-line no-param-reassign
        el.style.display = 'none';
      },
    );
    Array.from(document.getElementsByClassName('app-version-restriction')).forEach(el => {
      const buildNum = parseInt(window.App.build, 10);
      const minAndroidBuild = parseInt(el.dataset.minAndroidBuild, 10);
      const minIosBuild = parseInt(el.dataset.minIosBuild, 10);
      if (
        buildNum > 0 &&
        ((isAndroidApp() && buildNum < minAndroidBuild) || (isIosApp() && buildNum < minIosBuild))
      ) {
        // eslint-disable-next-line no-param-reassign
        el.style.display = 'none';
      }
    });
  });

  // Init any eventbrite embeds
  useEffect(
    () => {
      if (window.EBWidgets) {
        const ebEmbeds = document.querySelectorAll('div[id^="eventbrite-widget-container-"]');
        Array.from(ebEmbeds).forEach(div => {
          if (div.childNodes.length === 0) {
            const eventId = div.id.replace('eventbrite-widget-container-', '');
            if (isApp()) {
              const icon = document.createElement('div');
              icon.innerHTML =
                '<svg viewBox="0 0 100 98"><g fill-rule="evenodd"><g><g><path d="M44.053 23.401c10.985-2.481 21.946 2.016 28.049 10.52L23.556 44.89C25.23 34.627 33.068 25.882 44.053 23.4zm28.32 40.35c-3.67 5.322-9.329 9.35-16.227 10.908-11.057 2.498-22.09-2.076-28.169-10.69l48.698-11.002 7.931-1.792 15.165-3.426a47.9 47.9 0 00-1.263-9.656C92.336 11.931 65.658-4.382 38.923 1.658 12.188 7.698-4.482 33.804 1.691 59.967c6.172 26.162 32.85 42.475 59.585 36.435 15.753-3.559 28.007-14.085 34.156-27.484l-23.059-5.167z"></path></g></g></g></svg>';
              icon.classList.add('blog-post__eventbrite-fallback__icon');

              const text = document.createElement('p');
              text.classList.add('blog-post__eventbrite-fallback__message');
              text.innerText = div.dataset.altText || 'Get tickets for this event on Eventbrite';

              const button = document.createElement('a');
              button.href = `https://www.eventbrite.co.uk/e/${eventId}`;
              button.target = '_blank';
              button.rel = 'noopener noreferrer';
              button.innerText = div.dataset.altButtonText || 'Get tickets';
              button.classList.add('blog-post__eventbrite-fallback__button');

              const fallbackContent = document.createElement('div');
              fallbackContent.classList.add('blog-post__eventbrite-fallback');
              fallbackContent.appendChild(icon);
              fallbackContent.appendChild(text);
              fallbackContent.appendChild(button);
              div.appendChild(fallbackContent);
            } else {
              const embedConfig = {
                widgetType: div.dataset.widgetType || 'checkout',
                eventId,
                iframeContainerId: div.id,
                iframeContainerHeight: parseInt(div.dataset.iframeContainerHeight, 10) || 425,
              };
              window.EBWidgets.createWidget(embedConfig);
            }
          }
        });
      }
    },
    [loadedScripts],
  );

  useEffect(() => {
    const instagramPosts = document.querySelectorAll('blockquote[class="instagram-media"]');
    Array.from(instagramPosts)
      .filter(embed => !embed.closest('[data-ckbk-instagram="true"]'))
      .forEach(embed => {
        const div = document.createElement('div');
        div.setAttribute('data-ckbk-instagram', 'true');
        const root = createRoot(div);
        const url = embed.getAttribute('data-instgrm-permalink');
        if (url) {
          root.render(
            <Provider store={store}>
              <InstagramPost url={url} />
            </Provider>,
          );
        }
        const parent = embed.parentNode;
        parent.insertBefore(div, embed);
        parent.removeChild(embed);

        return root;
      });
  });

  const { title, body, asset_url, url_id, description, publish_on, tags, categories } =
    data && data.BlogPost ? data.BlogPost : {};

  const url = route.blogPost(url_id);
  if (error) {
    return <ErrorRouter error={error} />;
  }
  return (
    <div className="blog-post">
      <HeadContent
        contents={{
          title,
          description,
          content: asset_url,
          url,
        }}
      />
      <Offline
        connecting={loading}
        worksOffline={!!(data && data.BlogPost)}
        error={error}
        onReconnect={refetch}
      >
        <p>
          <Link to={route.blogPosts()} className="link link--energized heading heading--size-32">
            {loading && !title ? <Skeleton width="115px" /> : 'Features & Stories'}
          </Link>
        </p>
        <Heading level={1} className="heading heading--size-32">
          {loading && !title ? <Skeleton width="80%" /> : title}
        </Heading>
        <div className="blog-post__info-row">
          <div>
            <p>
              {publish_on ? longDate(publish_on) : <Skeleton width={100} />}
              {categories ? (
                categories.map(category => (
                  <React.Fragment>
                    {' '}
                    ·{' '}
                    <Link
                      to={route.blogPosts({ category })}
                      className="link link--energized"
                      key={category}
                    >
                      {category}
                    </Link>
                  </React.Fragment>
                ))
              ) : (
                <Skeleton width={160} />
              )}
            </p>
            <div className="dash" />
          </div>
          <div className="blog-post__share">
            {!loading ? (
              <ContentOptions
                heading={title}
                textToShare={title}
                urlToShare={`${baseUrl}${url}`}
                imageToShare={asset_url}
                subMenu="share"
                showEmbed={false}
              >
                <Icon
                  fill={COLOURS.ENERGIZED}
                  name="native-share"
                  width="20"
                  style={{ marginTop: -3 }}
                />
              </ContentOptions>
            ) : null}
          </div>
        </div>
        {loading && !body ? (
          <div>
            <Heading level={2} className="margin-bottom-20">
              <Skeleton width="50%" />
            </Heading>

            <p>
              <Skeleton count={5} width="100%" />
              <Skeleton width="20%" />
            </p>
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Skeleton width="150px" height="200px" />
            </div>

            <p className="margin-top-10">
              <Skeleton count={4} />
              <Skeleton width="20%" />
            </p>
          </div>
        ) : (
          <React.Fragment>
            <div
              ref={containerRef}
              className="blog-post__body"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: body,
              }}
            />
            <UnorderedList className="margin-top-20" inline spaceBetween="none">
              {tags.map(tag => (
                <ListItem key={tag}>
                  <Button
                    onClick={() => history.push(route.blogPosts({ tag }))}
                    className="margin-right-5 margin-bottom-5"
                    themeType="outline"
                    themeName="energized"
                    size="large"
                  >
                    {tag}
                  </Button>
                </ListItem>
              ))}
            </UnorderedList>
          </React.Fragment>
        )}
      </Offline>
    </div>
  );
}

BlogPost.propTypes = {
  id: PropTypes.string.isRequired,
};

export default BlogPost;
